import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { dataActionCreators, Project} from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { useEditorStyles } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Label, Link, Switch, SwitchOnChangeData, Text, Toast, ToastBody, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { useCustomers } from '../Tools/ObjectLoader';

export const ProjectDeleteConfirm = () => {
    // All data needed for edition should be loaded before calling the editor
    const projectDelete = useSelector<ApplicationState, Project | null>((state: ApplicationState) => state.data.projectDelete);
    const { customers } = useCustomers();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t, i18n } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Form validation
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    // Dependencies
    const customer: Customer | undefined = React.useMemo(
        () => {
            return customers && projectDelete ? customers.find((c) => projectDelete && c.customerid === projectDelete.customerid) : undefined;
        }, [customers, projectDelete]);

    if (projectDelete === null) {
        return null;
    }

    const submit = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                {i18n.t('DeletingProject', { ns: "Toasts" })}
                            </ToastTitle>
                            <ToastBody>
                                <div className={styles.Vertical}>
                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                    <Text>{t('Project') + " " + (projectDelete ? projectDelete.displayName : "?")}</Text>
                                </div>
                            </ToastBody>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/Projects/" + (projectDelete && projectDelete.projectid !== null ? projectDelete.projectid : ""), {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('DeletingProjectSucceed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{t('Project') + " " + (projectDelete ? projectDelete.displayName : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                if (projectDelete) dispatch(dataActionCreators.patchProjectDeletionEntryEdition(projectDelete));
                                dispatch(dataActionCreators.requestProjectDeletion(null));
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>{i18n.t('Dismiss', { ns: "Toasts" })}</Link>
                                                </ToastTrigger>
                                            }>
                                                {i18n.t('DeletingProjectFailed', { ns: "Toasts" })}
                                            </ToastTitle>
                                            <ToastBody>
                                                <div className={styles.Vertical}>
                                                    <Text>{t('Customer') + " " + (customer ? customer.displayName : "?")}</Text>
                                                    <Text>{t('Project') + " " + (projectDelete ? projectDelete.displayName : "?")}</Text>
                                                </div>
                                            </ToastBody>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>Delete timesheet</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestProjectDeletion(null));
                        }}
                        autoComplete="off"
                    >
                        <Field label={t('Customer')}>
                            <Label className={styles.label}>{customer && customer.displayName ? customer.displayName : "-"}</Label>
                        </Field>
                        <Field label={t('Project')}>
                            <Label className={styles.label}>{projectDelete && projectDelete.displayName ? projectDelete.displayName : "-"}</Label>
                        </Field>
                        <Field label="Description">
                            <Label className={styles.label}>{projectDelete && projectDelete.description ? projectDelete.description : ""}</Label>
                        </Field>
                        <Switch checked={confirmDelete} label={t('ConfirmDelete')} onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setConfirmDelete(data.checked); }} />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestProjectDeletion(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!confirmDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};